import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProducersMap } from "@/components/ProducersMap";
import { ArrowRight, Leaf, MapPin, ShieldCheck } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/36713207/pexels-photo-36713207.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800";
const STORY_IMG = "https://images.pexels.com/photos/29812817/pexels-photo-29812817.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const BENTO_IMG = "https://images.pexels.com/photos/17556893/pexels-photo-17556893.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

export default function Home() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [allVendors, setAllVendors] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { sort: "rating", limit: 6 } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/vendors").then((r) => { setAllVendors(r.data); setVendors(r.data.slice(0, 3)); }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Olive grove" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/30 to-bone" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-32 lg:pt-36 lg:pb-44">
          <p className="overline text-bone/80" data-testid="hero-overline">{t("home.heroOverline")}</p>
          <h1 className="font-serif text-5xl sm:text-7xl lg:text-[7.5rem] tracking-tight leading-[0.95] text-bone mt-6 max-w-4xl" data-testid="hero-title">
            {t("home.heroTitleLine1")}<br /><em className="font-light">{t("home.heroTitleLine2")}</em>
          </h1>
          <p className="text-bone/85 text-lg max-w-xl mt-8 leading-relaxed" data-testid="hero-subtitle">
            {t("home.heroSubtitle")}
          </p>
          <div className="flex flex-wrap gap-4 mt-12">
            <Link to="/shop" className="bg-olive text-bone px-8 py-4 rounded-sm hover:bg-olive-dark transition-colors text-sm tracking-wide" data-testid="hero-shop-cta">
              {t("home.exploreCellar")}
            </Link>
            <Link to="/vendors" className="border border-bone/60 text-bone px-8 py-4 rounded-sm hover:bg-bone/10 transition-colors text-sm tracking-wide" data-testid="hero-vendors-cta">
              {t("home.meetProducers")}
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-edge bg-boneAlt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
          {[
            { icon: <Leaf className="w-5 h-5 text-olive" />, t: t("home.trust1Title"), s: t("home.trust1Sub") },
            { icon: <MapPin className="w-5 h-5 text-olive" />, t: t("home.trust2Title"), s: t("home.trust2Sub") },
            { icon: <ShieldCheck className="w-5 h-5 text-olive" />, t: t("home.trust3Title"), s: t("home.trust3Sub") },
          ].map((it, i) => (
            <div key={i} className="flex items-start gap-3 justify-center md:justify-start">
              <span className="mt-1">{it.icon}</span>
              <div>
                <p className="font-medium">{it.t}</p>
                <p className="text-sm text-ink-soft">{it.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="overline">{t("home.featuredOverline")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">{t("home.featuredTitle")}</h2>
          </div>
          <Link to="/shop" className="text-sm flex items-center gap-2 hover:text-olive" data-testid="home-view-all">
            {t("home.seeAll")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="featured-products">
          {products.map((p) => <ProductCard key={p.product_id} product={p} testid="featured-product" />)}
        </div>
      </section>

      {/* BENTO STORY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 lg:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          <div className="md:col-span-7 relative rounded-sm overflow-hidden">
            <img src={STORY_IMG} alt="Producer" className="w-full h-[480px] object-cover" />
          </div>
          <div className="md:col-span-5 bg-boneAlt rounded-sm p-10 flex flex-col justify-center">
            <p className="overline">{t("home.whyTitle")}</p>
            <h3 className="font-serif text-4xl tracking-tight mt-4 leading-tight">
              {t("home.whyHeading")}
            </h3>
            <p className="text-ink-soft mt-6 leading-relaxed">
              {t("home.whyBody")}
            </p>
            <Link to="/vendors" className="overline mt-8 text-olive hover:underline" data-testid="story-vendors-link">
              {t("home.meetProducersLink")}
            </Link>
          </div>
        </div>
      </section>

      {/* PRODUCERS MAP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 lg:pb-32" data-testid="home-map-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline">{t("home.mapOverline")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">{t("home.mapTitle")}</h2>
            <p className="text-ink-soft mt-3 max-w-xl">{t("home.mapBody")}</p>
          </div>
          <Link to="/vendors" className="text-sm flex items-center gap-2 hover:text-olive">
            {t("home.seeAllProducers")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <ProducersMap vendors={allVendors} height="500px" />
      </section>

      {/* PRODUCERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 lg:pb-32">
        <p className="overline">{t("home.producersOverline")}</p>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 mb-12">{t("home.producersTitle")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" data-testid="featured-vendors">
          {vendors.map((v) => (
            <Link key={v.vendor_id} to={`/vendor/${v.vendor_id}`} className="group block bg-white hairline rounded-sm overflow-hidden hover:-translate-y-1 transition-all" data-testid={`vendor-card-${v.vendor_id}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={v.cover_image ? fileUrl(v.cover_image) : BENTO_IMG} alt={v.farm_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <p className="overline">{v.region}</p>
                <h3 className="font-serif text-2xl mt-2">{v.farm_name}</h3>
                <p className="text-sm text-ink-soft mt-3 line-clamp-3">{v.story}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-olive text-bone">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="overline text-bone/80">{t("home.ctaOverline")}</p>
            <h3 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4 leading-tight">
              {t("home.ctaHeading")}
            </h3>
          </div>
          <div className="md:text-right">
            <p className="text-bone/80 max-w-md md:ml-auto leading-relaxed">
              {t("home.ctaBody")}
            </p>
            <Link to="/become-vendor" className="inline-block mt-8 bg-bone text-olive px-8 py-4 rounded-sm hover:bg-boneAlt transition-colors text-sm" data-testid="cta-become-vendor">
              {t("home.sellOnOlivara")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
